// src/components/ReturnsCylinderEntryV2.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import ReturnsPhotosV2 from "./ReturnsPhotosV2";
import { useParams } from "react-router-dom";




const RETURNS_COL = "returns_v2";

/* ---- keep your exact config from ReturnsEntry.jsx ---- */
const SECTIONS = [
  { id: "return-cylinder", label: "Return & Cylinder" },
  { id: "reasoning", label: "Reasons & Fault Found" },
  { id: "cyl-checklist", label: "Cylinder Checklist" },
  { id: "leak-checklist", label: "Leak Checklist" },
  { id: "parts", label: "Parts Return" },
  { id: "operators", label: "Operators" },
  { id: "notes", label: "Notes" },
  { id: "photos", label: "Photos" },
];

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "waiting_collection", label: "Waiting for collection" },
  { value: "waiting_processing", label: "Waiting to be processed" },
  { value: "in_progress", label: "In progress" },
  { value: "straggler", label: "Straggler" },
];

const INDIRECT_PARTS = ["Inlet Group", "Tundish", "Expansion Vessel", "2 Port Valve", "Dual Stat", "Immersion"];
const DIRECT_PARTS = ["Inlet Group", "Tundish", "Expansion Vessel", "Smart Immersion", "Immersion"];

const LEAK_OPTIONS = [
  { key: "verticalSeam", label: "Vertical Seam" },
  { key: "statPocket", label: "Stat Pocket" },
  { key: "topSeam", label: "Top Seam" },
  { key: "coilBoss", label: "Coil Boss" },
  { key: "bottomSeam", label: "Bottom Seam" },
  { key: "coilBody", label: "Coil Body" },
  { key: "hotDrawOff", label: "Hot Draw Off" },
  { key: "bodySheet", label: "Body Sheet" },
  { key: "secondaryReturn", label: "Secondary Return" },
  { key: "tAndPRelief", label: "T&P Relief" },
  { key: "coldFeed", label: "Cold Feed" },
  { key: "immersionBoss", label: "Immersion Boss" },
  { key: "other", label: "OTHER" },
];

const OPERATOR_FIELDS = [
  { key: "welded", label: "Welded" },
  { key: "airTest", label: "Air Tested" },
  { key: "hotTested", label: "Hot Tested" },
  { key: "foamTested", label: "Tested (Foamed)" },
];

const INITIAL_FORM = {
  returnsNoteNumber: "",
  serialNumber: "",
  sretNumber: "",
  cylinderModel: "",
  capacity: "",
  dateOfManufacture: "",
  reasonCode: "",
  reasonDefectCode: "",
  faultFoundCode: "",
  faultDefectCode: "",
  operators: { welded: "", airTest: "", hotTested: "", foamTested: "" },
  internalNotes: "",
};

const INITIAL_PARTS = {
  noPartsReturned: false,
  allPartsReturned: false,
  partialReturn: false,
  systemType: "",
  partialBaseSelection: {},
  additionalParts: [],
  notes: "",
  rmaRef: "",
};

const INITIAL_LEAKS = (() => {
  const obj = {};
  LEAK_OPTIONS.forEach(({ key }) => (obj[key] = false));
  obj.otherComments = "";
  return obj;
})();

const INITIAL_CYL = {
  cylinderInstalled: false,
  damageToCase: false,
  damageToPackaging: false,
  damageToBosses: false,
  damageToPipeworkParts: false,
  damageToFoam: false,
  stripAndTest: false,
};

const COUNTERS_REF = doc(db, "appData", "counters");

function formatReturnsNoteNumber(n) {
  return `RN-${String(n).padStart(6, "0")}`;
}

async function getNextReturnsNumber() {
  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(COUNTERS_REF);
    if (!snap.exists()) {
      tx.set(COUNTERS_REF, { despatchCounter: 0, returnsCounter: 1 });
      return 1;
    }
    const cur = Number(snap.data().returnsCounter || 0);
    const n = cur + 1;
    tx.update(COUNTERS_REF, { returnsCounter: n });
    return n;
  });
  return next;
}

const useDebouncedEffect = (fn, deps, delay) => {
  useEffect(() => {
    const t = setTimeout(fn, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

const labelize = (k) => k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

export default function ReturnsCylinderEntryV2() {
  const navigate = useNavigate();
  const { search } = useLocation();

  // support both ?id= and old ?draft=
  const qs = useMemo(() => new URLSearchParams(search), [search]);
  const { id: routeId } = useParams();
  const urlId = routeId || qs.get("id") || qs.get("draft");

  const [form, setForm] = useState(INITIAL_FORM);
  const [parts, setParts] = useState(INITIAL_PARTS);
  const [leaks, setLeaks] = useState(INITIAL_LEAKS);
  const [cylCheck, setCylCheck] = useState(INITIAL_CYL);

  const [status, setStatus] = useState("new");
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(null);

  const [reasonOptions, setReasonOptions] = useState([]);
  const [defectOptions, setDefectOptions] = useState([]);

  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [errors, setErrors] = useState({});

  const [returnsId, setReturnsId] = useState(urlId || null);

  // dirty tracking
  const snapshotKey = useMemo(
    () => JSON.stringify({ form, parts, leaks, cylCheck, status }),
    [form, parts, leaks, cylCheck, status]
  );
  const [lastSavedKey, setLastSavedKey] = useState(snapshotKey);
  const dirty = snapshotKey !== lastSavedKey;

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);

  // side-nav highlight
  const observer = useRef(null);
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -70% 0px", threshold: 0 };
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id));
    }, opts);

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.current.observe(el);
    });

    return () => observer.current?.disconnect();
  }, []);

  // load code tables
  useEffect(() => {
    (async () => {
      const rs = await getDocs(query(collection(db, "return_reasons")));
      setReasonOptions(rs.docs.map((d) => d.data()).filter(Boolean));

      const ds = await getDocs(query(collection(db, "defect_codes")));
      setDefectOptions(ds.docs.map((d) => d.data()).filter(Boolean));
    })();
  }, []);

  // load existing record (single collection)
  useEffect(() => {
    (async () => {
      if (!urlId) return;

      const snap = await getDoc(doc(db, RETURNS_COL, urlId));
      if (!snap.exists()) {
        navigate("/returns/cyl/new", { replace: true });
        return;
      }

      const d = snap.data();

      if (d.type && d.type !== "cyl") {
        alert("That record is not a cylinder return.");
        navigate("/returns/cyl/new", { replace: true });
        return;
      }

      const loadedForm = d.cyl?.form || INITIAL_FORM;
      const loadedParts = d.cyl?.parts || INITIAL_PARTS;
      const loadedLeaks = d.cyl?.leaks || INITIAL_LEAKS;
      const loadedCheck = d.cyl?.cylCheck || INITIAL_CYL;

      setForm(loadedForm);
      setParts(loadedParts);
      setLeaks(loadedLeaks);
      setCylCheck(loadedCheck);

      setReturnsId(urlId);
      setStatus(d.status || "new");
      setStatusUpdatedAt(d.statusUpdatedAt || null);

      setLastSavedKey(
        JSON.stringify({
          form: loadedForm,
          parts: loadedParts,
          leaks: loadedLeaks,
          cylCheck: loadedCheck,
          status: d.status || "new",
        })
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  const getReasonMeta = (code) => reasonOptions.find((r) => r.code === code);
  const reasonRequiresDefect = !!getReasonMeta(form.reasonCode)?.requiresDefect;
  const faultRequiresDefect = !!getReasonMeta(form.faultFoundCode)?.requiresDefect;

  // progress (simple but useful)
  const progress = useMemo(() => {
    let s = 0;
    if (form.returnsNoteNumber) s += 10;
    if (form.serialNumber) s += 12;
    if (form.cylinderModel) s += 12;

    if (form.reasonCode) s += 12;
    if (!reasonRequiresDefect || form.reasonDefectCode) s += 8;

    if (form.faultFoundCode) s += 12;
    if (!faultRequiresDefect || form.faultDefectCode) s += 8;

    const picked = [parts.noPartsReturned, parts.allPartsReturned, parts.partialReturn].filter(Boolean).length;
    if (picked > 0) s += 8;

    if (!leaks.other || (leaks.other && leaks.otherComments)) s += 8;

    return Math.min(100, s);
  }, [form, parts, leaks, reasonRequiresDefect, faultRequiresDefect]);

  const getSystemList = (systemType) =>
    systemType === "indirect" ? INDIRECT_PARTS : systemType === "direct" ? DIRECT_PARTS : [];

  const buildReturnedParts = (p) => {
    if (p.noPartsReturned) return [];
    const base = getSystemList(p.systemType);
    if (p.allPartsReturned) return [...base];
    const chosenBase = Object.entries(p.partialBaseSelection || {})
      .filter(([, v]) => !!v)
      .map(([name]) => name);
    const extra = (p.additionalParts || []).map((x) => String(x).trim()).filter(Boolean);
    return [...chosenBase, ...extra];
  };

  const validate = () => {
    const e = {};
    if (!form.serialNumber?.trim()) e.serialNumber = "Serial number is required.";
    if (!form.cylinderModel?.trim()) e.cylinderModel = "Cylinder model is required.";

    if (!form.reasonCode) e.reasonCode = "Pick a return reason.";
    if (reasonRequiresDefect && !form.reasonDefectCode) e.reasonDefectCode = "Pick a defect for this reason.";

    if (!form.faultFoundCode) e.faultFoundCode = "Pick a fault found code.";
    if (faultRequiresDefect && !form.faultDefectCode) e.faultDefectCode = "Pick a defect for this fault.";

    // parts mode sanity
    const picked = [parts.noPartsReturned, parts.allPartsReturned, parts.partialReturn].filter(Boolean).length;
    if (picked === 0) e.partsMode = "Select: No parts, All parts, or Partial.";
    if (picked > 1) e.partsMode = "Only one parts option can be selected.";

    if ((parts.allPartsReturned || parts.partialReturn) && !parts.systemType) e.systemType = "Select Direct or Indirect.";

    if (parts.partialReturn && parts.systemType) {
      const anyBase = Object.values(parts.partialBaseSelection || {}).some(Boolean);
      const anyAdditional = (parts.additionalParts || []).some((x) => !!String(x).trim());
      if (!anyBase && !anyAdditional) e.partialItems = "Tick at least one part (or add an additional part).";
    }

    if (leaks.other && !leaks.otherComments?.trim()) e.leakOther = "Add details for 'OTHER'.";
    return e;
  };
  

  const ensureExists = async () => {
    if (returnsId) return returnsId;

    let ensured = form;
    if (!ensured.returnsNoteNumber) {
      const nextNum = await getNextReturnsNumber();
      ensured = { ...ensured, returnsNoteNumber: formatReturnsNoteNumber(nextNum) };
      setForm(ensured);
    }

    const created = await addDoc(collection(db, RETURNS_COL), {
      type: "cyl",
      status,
      statusUpdatedAt: statusUpdatedAt || serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      submittedAt: null,
      archive: false,
      archivedAt: null,
      progress,
      source: "pthelpdesk",
      version: 1,
      cyl: {
        form: ensured,
        parts,
        leaks,
        cylCheck,
        returnedParts: buildReturnedParts(parts),
      },
    });


    setReturnsId(created.id);

    const q = new URLSearchParams(window.location.search);
    q.set("id", created.id);
    navigate({ pathname: "/returns/cyl/new", search: `?${q.toString()}` }, { replace: true });

    // create sharepoint folder (non-fatal)
    try {
      const create = httpsCallable(fns, "spEnsureFolder");
      const { data } = await create({ type: "returnsImage", subId: created.id });
      await updateDoc(doc(db, RETURNS_COL, created.id), {
        storage: { provider: "sharepoint", folderId: data.folderItemId, folderUrl: data.folderWebUrl },
      });
    } catch (e) {
      console.error("Folder create failed:", e);
    }

    return created.id;
  };

  const save = async (manual = false) => {
    try {
      setSaveError("");
      setIsSaving(true);

      const id = await ensureExists();
      await updateDoc(doc(db, RETURNS_COL, id), {
        type: "cyl",
        status,
        statusUpdatedAt: statusUpdatedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        progress,
        cyl: {
          form,
          parts,
          leaks,
          cylCheck,
          returnedParts: buildReturnedParts(parts),
        },
      });

      setLastSavedKey(snapshotKey);
      if (manual) console.info("Saved.");
    } catch (e) {
      console.error(e);
      setSaveError("Could not save.");
    } finally {
      setIsSaving(false);
    }
  };

  // autosave
  useDebouncedEffect(() => {
    if (!dirty) return;
    if (isSaving || isSubmitting) return;
    if (!returnsId && progress === 0) return; // avoid creating empty docs
    save(false);
  }, [snapshotKey], 1500);

  // warn on close if dirty
  useEffect(() => {
    const handler = (e) => {
      if (!dirty || isSubmitting) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, isSubmitting]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const eMap = validate();
    setErrors(eMap);

    if (Object.keys(eMap).length) {
      const first = Object.keys(eMap)[0];
      const sectionMap = {
        serialNumber: "return-cylinder",
        cylinderModel: "return-cylinder",
        reasonCode: "reasoning",
        reasonDefectCode: "reasoning",
        faultFoundCode: "reasoning",
        faultDefectCode: "reasoning",
        partsMode: "parts",
        systemType: "parts",
        partialItems: "parts",
        leakOther: "leak-checklist",
      };
      const target = sectionMap[first] || "return-cylinder";
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    try {
      setIsSubmitting(true);
      const id = await ensureExists();
      await updateDoc(doc(db, RETURNS_COL, id), {
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (!window.confirm("Submitted. Start a new report?")) return;
      navigate("/returns/cyl/new", { replace: true });
      window.location.reload();
    } catch (err) {
      console.error(err);
      setErrors((p) => ({ ...p, submit: "Submit failed. Try again." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const systemParts = useMemo(() => getSystemList(parts.systemType), [parts.systemType]);

  const togglePartsMain = (key) => {
    setParts((prev) => ({
      ...prev,
      noPartsReturned: false,
      allPartsReturned: false,
      partialReturn: false,
      [key]: !prev[key],
    }));
  };

  const setSystemType = (sysType) => {
    setParts((prev) => {
      const baseList = getSystemList(sysType);
      const baseMap = Object.fromEntries(baseList.map((n) => [n, false]));
      return { ...prev, systemType: sysType, partialBaseSelection: baseMap };
    });
  };

  const togglePartialBase = (name) => {
    setParts((prev) => ({
      ...prev,
      partialBaseSelection: {
        ...(prev.partialBaseSelection || {}),
        [name]: !prev?.partialBaseSelection?.[name],
      },
    }));
  };

  const addAdditionalPart = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    setParts((prev) => ({
      ...prev,
      additionalParts: [...(prev.additionalParts || []), clean],
    }));
  };

  const removeAdditionalPart = (idx) => {
    setParts((prev) => {
      const arr = [...(prev.additionalParts || [])];
      arr.splice(idx, 1);
      return { ...prev, additionalParts: arr };
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h1 className="text-3xl font-bold text-gray-900">Returns — Cylinder (V2)</h1>

        <div className="ml-0 sm:ml-4">
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setStatusUpdatedAt(new Date().toISOString());
            }}
            className="text-sm rounded-md border border-gray-300 px-3 py-2"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <span className="ml-auto text-sm text-gray-500">
          {isSaving ? "Saving…" : dirty ? "Unsaved changes" : returnsId ? "All changes saved" : "Nothing saved yet"}
        </span>

        {saveError && <span className="text-sm text-red-600">{saveError}</span>}

        <button
          type="button"
          onClick={() => save(true)}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Save
        </button>

        <button
          type="button"
          onClick={() => navigate("/ReturnsViewerV2")}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Side nav */}
        <aside className="lg:sticky lg:top-4 h-max bg-white border border-gray-200 rounded-xl p-3">
          <nav className="space-y-1">
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                className={`w-full text-left px-3 py-2 rounded-md transition ${
                  activeId === id ? "bg-indigo-600 text-white" : "hover:bg-gray-100 text-gray-800"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="mt-4 p-3 bg-gray-50 border border-dashed rounded-md text-xs text-gray-600">
            <div>
              <b>ID:</b> {returnsId ? returnsId.slice(-8) : "none"}
            </div>
            <div>
              <b>RN:</b> {form.returnsNoteNumber || "not allocated yet"}
            </div>
            <div>
              <b>Progress:</b> {Math.round(progress)}%
            </div>
          </div>
        </aside>

        {/* Main form */}
        <form onSubmit={onSubmit} className="space-y-10">
          {/* Return & Cylinder */}
          <Section id="return-cylinder" title="Return & Cylinder">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Returns Note Number" value={form.returnsNoteNumber} onChange={() => {}} readOnly />

              <TextField
                label="Serial Number"
                value={form.serialNumber}
                onChange={(v) => setForm((p) => ({ ...p, serialNumber: v }))}
                error={errors.serialNumber}
              />

              <TextField
                label="SRET Number"
                value={form.sretNumber}
                onChange={(v) => setForm((p) => ({ ...p, sretNumber: v }))}
              />

              <TextField
                label="Cylinder Model"
                value={form.cylinderModel}
                onChange={(v) => setForm((p) => ({ ...p, cylinderModel: v }))}
                error={errors.cylinderModel}
              />

              <TextField
                label="Capacity"
                value={form.capacity}
                onChange={(v) => setForm((p) => ({ ...p, capacity: v }))}
                placeholder="e.g., 200L"
              />

              <TextField
                label="Date of Manufacture"
                type="date"
                value={form.dateOfManufacture}
                onChange={(v) => setForm((p) => ({ ...p, dateOfManufacture: v }))}
              />
            </div>
          </Section>

          {/* Reasons & Fault Found */}
          <Section id="reasoning" title="Reasons & Fault Found (coded)">
            <div className="grid sm:grid-cols-2 gap-4">
              <CodeSelect
                label="Return Reason"
                value={form.reasonCode}
                onChange={(code) => setForm((p) => ({ ...p, reasonCode: code, reasonDefectCode: "" }))}
                options={reasonOptions.filter((r) => !r.deprecated)}
                getOptionLabel={(r) => `${r.code} — ${r.description}`}
                getOptionValue={(r) => r.code}
                placeholder="Search code or description…"
                error={errors.reasonCode}
              />

              {reasonRequiresDefect && (
                <CodeSelect
                  label="Reason Defect"
                  value={form.reasonDefectCode}
                  onChange={(code) => setForm((p) => ({ ...p, reasonDefectCode: code }))}
                  options={defectOptions.filter((d) => !d.deprecated)}
                  getOptionLabel={(d) => `${d.code} — ${d.description}`}
                  getOptionValue={(d) => d.code}
                  placeholder="Search defect…"
                  error={errors.reasonDefectCode}
                />
              )}

              <CodeSelect
                label="Fault Found"
                value={form.faultFoundCode}
                onChange={(code) => setForm((p) => ({ ...p, faultFoundCode: code, faultDefectCode: "" }))}
                options={reasonOptions.filter((r) => !r.deprecated)}
                getOptionLabel={(r) => `${r.code} — ${r.description}`}
                getOptionValue={(r) => r.code}
                placeholder="Search code or description…"
                error={errors.faultFoundCode}
              />

              {faultRequiresDefect && (
                <CodeSelect
                  label="Fault Defect"
                  value={form.faultDefectCode}
                  onChange={(code) => setForm((p) => ({ ...p, faultDefectCode: code }))}
                  options={defectOptions.filter((d) => !d.deprecated)}
                  getOptionLabel={(d) => `${d.code} — ${d.description}`}
                  getOptionValue={(d) => d.code}
                  placeholder="Search defect…"
                  error={errors.faultDefectCode}
                />
              )}
            </div>
          </Section>

          {/* Cylinder Checklist */}
          <Section id="cyl-checklist" title="Cylinder Checklist">
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.keys(cylCheck).map((k) => (
                <CheckRow
                  key={k}
                  label={labelize(k)}
                  checked={!!cylCheck[k]}
                  onChange={() => setCylCheck((p) => ({ ...p, [k]: !p[k] }))}
                />
              ))}
            </div>
          </Section>

          {/* Leak Checklist */}
          <Section id="leak-checklist" title="Leak Checklist">
            <div className="grid sm:grid-cols-2 gap-3">
              {LEAK_OPTIONS.map(({ key, label }) =>
                key !== "other" ? (
                  <CheckRow
                    key={key}
                    label={label}
                    checked={!!leaks[key]}
                    onChange={() => setLeaks((p) => ({ ...p, [key]: !p[key] }))}
                  />
                ) : null
              )}

              <CheckRow
                label="OTHER"
                checked={!!leaks.other}
                onChange={() => setLeaks((p) => ({ ...p, other: !p.other }))}
              />
            </div>

            {leaks.other && (
              <div className="mt-4">
                <TextField
                  label="Other Comments"
                  value={leaks.otherComments}
                  onChange={(v) => setLeaks((p) => ({ ...p, otherComments: v }))}
                  error={errors.leakOther}
                />
              </div>
            )}
          </Section>

          {/* Parts Return */}
          <Section id="parts" title="Parts Return">
            <div className="flex flex-wrap gap-3 mb-4">
              <TogglePill label="No parts returned" active={parts.noPartsReturned} onClick={() => togglePartsMain("noPartsReturned")} />
              <TogglePill label="All parts returned" active={parts.allPartsReturned} onClick={() => togglePartsMain("allPartsReturned")} />
              <TogglePill label="Partial return" active={parts.partialReturn} onClick={() => togglePartsMain("partialReturn")} />
            </div>

            {errors.partsMode && <FieldError text={errors.partsMode} />}

            {(parts.allPartsReturned || parts.partialReturn) && (
              <>
                <div className="flex gap-3 items-center mb-3">
                  <span className="text-sm text-gray-700">System type:</span>
                  <TogglePill label="Indirect" active={parts.systemType === "indirect"} onClick={() => setSystemType("indirect")} />
                  <TogglePill label="Direct" active={parts.systemType === "direct"} onClick={() => setSystemType("direct")} />
                </div>

                {errors.systemType && <FieldError text={errors.systemType} />}

                {parts.allPartsReturned && parts.systemType && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-700 font-medium mb-1">Items included:</p>
                    <ul className="list-disc pl-5 text-sm text-gray-800">
                      {systemParts.map((pname) => (
                        <li key={pname}>{pname}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {parts.partialReturn && parts.systemType && (
                  <>
                    <p className="text-sm text-gray-700 font-medium mb-1">Select returned parts:</p>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-2">
                      {systemParts.map((pname) => (
                        <CheckRow
                          key={pname}
                          label={pname}
                          checked={!!parts.partialBaseSelection?.[pname]}
                          onChange={() => togglePartialBase(pname)}
                        />
                      ))}
                    </div>

                    <AdditionalPartsEditor
                      items={parts.additionalParts || []}
                      onAdd={addAdditionalPart}
                      onRemove={removeAdditionalPart}
                    />

                    {errors.partialItems && <FieldError text={errors.partialItems} />}
                  </>
                )}
              </>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <TextField
                label="Parts Notes (optional)"
                value={parts.notes}
                onChange={(v) => setParts((p) => ({ ...p, notes: v }))}
              />
              <TextField
                label="RMA / Returns Ref (optional)"
                value={parts.rmaRef}
                onChange={(v) => setParts((p) => ({ ...p, rmaRef: v }))}
              />
            </div>
          </Section>

          {/* Operators */}
          <Section id="operators" title="Operators (Initials)">
            <div className="grid sm:grid-cols-2 gap-4">
              {OPERATOR_FIELDS.map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  value={form.operators?.[key] || ""}
                  onChange={(v) =>
                    setForm((prev) => ({
                      ...prev,
                      operators: { ...(prev.operators || {}), [key]: v },
                    }))
                  }
                  placeholder="e.g., PT"
                />
              ))}
            </div>
          </Section>

          {/* Notes */}
          <Section id="notes" title="Notes">
            <TextArea
              label="Internal Notes"
              value={form.internalNotes}
              onChange={(v) => setForm((p) => ({ ...p, internalNotes: v }))}
              placeholder="Anything extra the workshop / tech team should know…"
            />
          </Section>

          {/* Photos */}
          <ReturnsPhotosV2 returnsId={returnsId} returnsCol={RETURNS_COL} />

          {/* Sticky submit */}
          <div className="h-16" />
          <div className="fixed bottom-4 left-0 right-0 px-4">
            <div className="max-w-7xl mx-auto bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-center gap-3">
              <span className="text-sm text-gray-600">
                ID: {returnsId ? returnsId.slice(-8) : "none"} · Status{" "}
                {STATUS_OPTIONS.find((s) => s.value === status)?.label || status} · Progress{" "}
                {Math.round(progress)}%
              </span>

              <button
                type="button"
                onClick={() => save(true)}
                className="ml-auto px-4 py-3 rounded-md border border-gray-300 hover:bg-gray-50"
                disabled={isSaving}
              >
                {isSaving ? "Saving…" : "Save"}
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-md text-white transition ${
                  isSubmitting ? "bg-indigo-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {isSubmitting ? "Submitting…" : "Submit Return"}
              </button>
            </div>
          </div>

          {errors.submit && <FieldError text={errors.submit} />}
          <div className="h-24" />
        </form>
      </div>
    </div>
  );
}

/* =========================================================
   UI BITS (same style as your current)
========================================================= */

function Section({ id, title, children }) {
  return (
    <section id={id} className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, placeholder = "", error, type = "text", readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        } ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
        value={value || ""}
        onChange={(e) => {
          if (!readOnly) onChange(e.target.value);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder = "", error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea
        rows={4}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        }`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
      <input type="checkbox" className="h-4 w-4" checked={!!checked} onChange={onChange} />
      <span className="text-sm text-gray-800">{label}</span>
    </label>
  );
}

function TogglePill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full border transition ${
        active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );
}

function FieldError({ text }) {
  return <p className="mt-2 text-sm text-red-600">{text}</p>;
}

/**
 * Searchable dropdown (keeps your existing UX).
 * Uses options array + label/value mappers.
 */
function CodeSelect({ label, value, onChange, options, getOptionLabel, getOptionValue, placeholder, error }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => getOptionLabel(o).toLowerCase().includes(term));
  }, [q, options, getOptionLabel]);

  const selected = options.find((o) => getOptionValue(o) === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div
        className={`w-full rounded-md border px-3 py-2 flex items-center justify-between cursor-pointer ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm text-gray-800">
          {selected ? getOptionLabel(selected) : <span className="text-gray-400">Select…</span>}
        </span>
        <svg className="w-4 h-4 opacity-70" viewBox="0 0 20 20">
          <path d="M5 7l5 5 5-5H5z" />
        </svg>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2">
            <input
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder={placeholder || "Search…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-56 overflow-auto">
            {list.length === 0 && <div className="p-3 text-sm text-gray-500">No matches</div>}
            {list.map((o) => {
              const val = getOptionValue(o);
              return (
                <button
                  key={val}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${val === value ? "bg-gray-100" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(val);
                    setOpen(false);
                    setQ("");
                  }}
                >
                  {getOptionLabel(o)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <FieldError text={error} />}
    </div>
  );
}

function AdditionalPartsEditor({ items, onAdd, onRemove }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="mt-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">Additional parts</label>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="Type a part name (e.g., PRV, Check Valve)…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const clean = draft.trim();
              if (clean) onAdd(clean);
              setDraft("");
            }
          }}
        />
        <button
          type="button"
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
          onClick={() => {
            const clean = draft.trim();
            if (clean) onAdd(clean);
            setDraft("");
          }}
        >
          Add
        </button>
      </div>

      {items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {items.map((name, idx) => (
            <span key={`${name}-${idx}`} className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm">
              {name}
              <button type="button" className="text-red-600" onClick={() => onRemove(idx)} title="Remove">
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
